import { useAuth } from 'auth/AuthProvider'
import BlueButton from 'components/inputs/BlueButton'
import NumberInput from 'components/inputs/NumberInput'
import PasswordInput from 'components/inputs/PasswordInput'
import MySpinner from 'components/MySpinner'
import React, { useCallback, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import {
  reqChangeClientPassword,
  reqSendChangePasswordCode,
} from '../client/ClientRequests'

interface IChangePasswordDialogProps {
  show: boolean
  onClose: () => void
}

const ChangePasswordDialog: React.FC<IChangePasswordDialogProps> = ({
  show,
  onClose,
}) => {
  const auth = useAuth()
  const [oldPassword, setOldPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [newPasswordSecond, setNewPasswordSecond] = useState<string>('')
  const [secondPasswordError, setSecondPasswordError] = useState<string>('')
  const [error, setError] = useState<string>('')

  const [code, setCode] = useState<number | undefined>(undefined)
  const [isCodeSending, setIsCodeSending] = useState(false)
  const [isCodeSend, setIsCodeSend] = useState(false)

  useEffect(() => {
    if (show) setError('')
  }, [show])

  useEffect(() => {
    if (newPassword.length > 0) {
      if (newPassword !== newPasswordSecond) {
        setSecondPasswordError('Пароли должны совпадать')
      } else {
        setSecondPasswordError('')
      }
    }
  }, [newPassword, newPasswordSecond])

  const handleChangeOldPassword = useCallback((value, isValid) => {
    if (isValid) setOldPassword(value)
    else setOldPassword('')
  }, [])

  const handleChangeCode = (value: number | '') => {
    if (value === '') {
      setCode(undefined)
    } else {
      setCode(value)
    }
  }

  const handleChangeNewPassword = useCallback((value, isValid) => {
    if (isValid) setNewPassword(value)
    else setNewPassword('')
  }, [])

  const handleChangeNewPasswordSecond = useCallback((value, isValid) => {
    if (isValid) setNewPasswordSecond(value)
    else setNewPasswordSecond('')

    if (!value) setSecondPasswordError('Повторите новый пароль')
  }, [])

  const handleSendCode = useCallback(() => {
    // console.log(loginPhone)
    setError('')
    setIsCodeSend(false)
    setIsCodeSending(true)
    reqSendChangePasswordCode((response) => {
      setIsCodeSending(false)
      if (response.status === 'INVALID_PHONE_NUMBER') {
        setError('Пользователь с таким номером не зарегистрирован')
      } else if (response.status !== 'OK') {
        setError('Невозможно отправить СМС-код подтверждения')
      } else {
        setIsCodeSend(true)
      }
    })
  }, [])

  const handleChange = useCallback(() => {
    if (auth.user && !auth.user.is_password_exists && !code) {
      setError('Введите код восстановления')
    } else if (auth.user && auth.user.is_password_exists && oldPassword.length === 0) {
      setError('Введите старый пароль')
    } else if (newPassword.length === 0) {
      setError('Введите новый пароль')
    } else if (newPassword !== newPasswordSecond) {
      setError('Пароли должны совпадать')
    } else {
      reqChangeClientPassword(
        oldPassword,
        code || -1,
        newPassword,
        newPasswordSecond,
        (response) => {
          if (response.status === 'OK') {
            auth.setUser(response.client)
            onClose()
          } else if (response.status === 'INVALID_OLD_PASSWORD') {
            setError('Неверный старый пароль')
          } else if (response.status === 'INVALID_NEW_PASSWORD') {
            setError('Введите другой пароль')
          } else if (response.status === 'SAME_AS_OLD') {
            setError('Новый и старый пароли не должны совпадать')
          } else if (response.status === 'NOT_SAME_PASSWORDS') {
            setError('Пароли должны совпадать')
          } else if (response.status === 'NOT_ALLOWED') {
            setError('Смена пароля доступна только из личного кабинета')
          } else if (response.status === 'BAD_CODE') {
            setError('Неверный код или его срок действия истек')
          } else {
            setError('Неизвестная ошибка')
          }
        }
      )
    }
  }, [auth, code, oldPassword, newPassword, newPasswordSecond, onClose])

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Изменение пароля</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          {auth.user?.is_password_exists ? (
            <>
              <div>Старый пароль:</div>
              <PasswordInput
                onChange={handleChangeOldPassword}
                maxLength={20}
                minLength={8}
                style={{ marginBottom: '2rem' }}
              />
            </>
          ) : (
            <>
              {isCodeSending ? (
                <>
                  <MySpinner />
                </>
              ) : !isCodeSend ? (
                <>
                  <div className='m-2'></div>
                  <div style={{ marginBottom: '1rem' }}>
                    <BlueButton onClick={handleSendCode} style={{ width: '100%' }}>
                      Отправить код восстановления
                    </BlueButton>
                  </div>
                </>
              ) : (
                <>
                  <p>Введите код восстановления</p>
                  <NumberInput
                    defaultValue={code}
                    onChange={handleChangeCode}
                    style={{ marginBottom: '2rem' }}
                  />
                </>
              )}
            </>
          )}

          <div>Новый пароль:</div>
          <PasswordInput
            required
            onChange={handleChangeNewPassword}
            maxLength={20}
            minLength={8}
            style={{ marginBottom: '2rem' }}
          />

          <div>Повторите новый пароль:</div>
          <PasswordInput
            required
            errorMessage={secondPasswordError}
            onChange={handleChangeNewPasswordSecond}
            onEntered={handleChange}
            maxLength={20}
            minLength={8}
            style={{ marginBottom: '2rem' }}
          />
          {error && <div style={{ color: 'var(--color-red)' }}>{error}</div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <BlueButton
          onClick={handleChange}
          disabled={!isCodeSend && !!auth.user && auth.user.is_password_exists === 0}
        >
          Изменить
        </BlueButton>
        <BlueButton onClick={onClose}>Отмена</BlueButton>
      </Modal.Footer>
    </Modal>
  )
}
export default ChangePasswordDialog
